import { useEffect, useCallback } from 'react';

import { SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, Navigate, useLocation } from 'react-router';

import { GlobalBanner } from '@/components/atoms/GlobalBanner/GlobalBanner';
import MainNavigationIframe from '@/components/organisms/IframeNavigation/MainNavigationIframe';
import { MainNavigation } from '@/components/organisms/MainNavigation/MainNavigation';
import { personalSettings } from '@/components/organisms/Navbar/SettingsNavigation/constants';
import { SettingsNavigation } from '@/components/organisms/Navbar/SettingsNavigation/SettingsNavigation';
import { PageSubHeader, PageSubHeaderBreadcrumbs } from '@/components/organisms/PageSubHeader';
import Profile from '@/components/pages/Profile/Profile';
import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { useAccount } from '@/hooks/useAccount';
import { usePermissions } from '@/hooks/usePermissions';
import { CommandPalette } from '@/modules/commandPalette/CommandPalette';
import { CommandPaletteProvider } from '@/modules/commandPalette/CommandPaletteProvider';
import Security from '@/modules/web-sessions/Security';
import { closeBanner } from '@/redux/banners/actions';
import { selectBanners } from '@/redux/banners/selectors';
import { setAccountRole, setPermissions } from '@/redux/permissions/actions';
import { setAccountId, setAccountSlug } from '@/redux/session/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { selectIframeView } from '@/redux/user/selectors';

const ProfileRouter: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const title_key = personalSettings.find((s) => location.pathname.includes(s.link))?.title_key;
  const dispatch = useDispatch();
  const { account, accountRole, lastUsedAccountSlug, setLastUsedAccountSlug } = useAccount();
  const { permissions } = usePermissions();
  const iframeView = useSelector(selectIframeView);
  const banners = useSelector(selectBanners);

  const sessionSlug = useSelector(selectAccountSlug);

  useEffect(() => {
    if (!sessionSlug) {
      dispatch(setAccountSlug(lastUsedAccountSlug));
    }
  }, [dispatch, lastUsedAccountSlug, sessionSlug]);

  useEffect(() => {
    if (account?.slug) {
      setLastUsedAccountSlug(account.slug);
    }
  }, [account?.slug, dispatch, setLastUsedAccountSlug]);

  useEffect(() => {
    if (account?.account_id) {
      dispatch(setAccountId(account?.account_id));
      dispatch(setAccountRole(accountRole));
      dispatch(setPermissions(permissions));
    }
  }, [dispatch, account?.account_id, accountRole, permissions]);

  const onBannerDismiss = useCallback(() => {
    dispatch(closeBanner());
  }, [dispatch]);

  return (
    <CommandPaletteProvider>
      <div className="flex h-screen flex-col">
        {banners.props && <GlobalBanner {...banners.props} onDismiss={onBannerDismiss} />}
        {!iframeView && <MainNavigation />}
        {iframeView && <MainNavigationIframe />}
        <Layout>
          <PageSubHeader
            breadcrumbs={
              <PageSubHeaderBreadcrumbs>
                <Breadcrumb>
                  <BreadcrumbList>
                    <BreadcrumbItem>
                      <BreadcrumbPage>
                        <div className="flex h-8 w-8 items-center justify-center">
                          <SettingsIcon size={16} className="text-muted-foreground" />
                        </div>
                        <div className="mr-4 ml-2 h-4 w-px bg-border" />
                        <span className="text-sm leading-5 font-normal">
                          {title_key ? t(title_key) : t('profile.personal_settings')}
                        </span>
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </BreadcrumbList>
                </Breadcrumb>
              </PageSubHeaderBreadcrumbs>
            }
          />
          <div className="flex min-h-0 flex-1">
            <SettingsNavigation />
            <div className="flex min-h-0 flex-1 flex-col">
              <PageContentWrapper className="pt-8">
                <Routes>
                  <Route path="/information" element={<Profile />} />
                  <Route
                    path="/invitations"
                    element={<Navigate to="/profile/information" replace />}
                  />
                  <Route
                    path="/notifications"
                    element={<Navigate to="/profile/information" replace />}
                  />
                  <Route path="/security" element={<Security />} />
                </Routes>
              </PageContentWrapper>
            </div>
          </div>
        </Layout>
        {!iframeView && <CommandPalette />}
      </div>
    </CommandPaletteProvider>
  );
};

export default ProfileRouter;
